import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Card, List, Spin, Tag, Typography } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import axios from '../../utils/axios';
import { LEGAL_HUB_ENTRIES, legalDocumentPath } from '../../utils/legalPublicUrls';

const { Text, Paragraph } = Typography;

/**
 * Реестр активных legal-документов платформы (P5-A).
 * Тексты — только на отдельных страницах через UAM API; здесь список + метаданные.
 */
export default function LegalDocumentsRegistryWidget() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get('/api/consents/documents', { params: { region: 'RU' } })
      .then((resp) => {
        if (cancelled) return;
        const list = Array.isArray(resp?.data?.data) ? resp.data.data : [];
        setDocuments(list);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить реестр документов.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const byType = useMemo(() => {
    const map = new Map();
    documents.forEach((d) => {
      if (d?.type) map.set(String(d.type), d);
    });
    return map;
  }, [documents]);

  const entries = useMemo(() => {
    const hubTypes = new Set(LEGAL_HUB_ENTRIES.map((e) => e.consentType));
    const rows = LEGAL_HUB_ENTRIES.map((entry) => {
      const doc = byType.get(entry.consentType);
      return {
        ...entry,
        title: doc?.title || entry.label,
        version: doc?.version,
        required: doc?.required,
        hasPublicPage: Boolean(entry.path),
      };
    });
    documents.forEach((doc) => {
      const type = String(doc.type || '');
      if (hubTypes.has(type)) return;
      const path = legalDocumentPath(type);
      if (!path) return;
      rows.push({
        consentType: type,
        label: doc.title || type,
        title: doc.title || type,
        path,
        version: doc.version,
        required: doc.required,
        hasPublicPage: true,
      });
    });
    return rows;
  }, [byType, documents]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" showIcon message={error} />;
  }

  return (
    <Card bordered={false} style={{ background: 'transparent' }}>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Актуальные версии документов оператора персональных данных. Полные тексты открываются на отдельных
        страницах и загружаются из единого источника (UAM).
      </Paragraph>
      <List
        itemLayout="horizontal"
        dataSource={entries}
        renderItem={(item) => (
          <List.Item
            actions={[
              item.hasPublicPage ? (
                <Link key="open" to={item.path}>
                  Открыть
                </Link>
              ) : null,
            ].filter(Boolean)}
          >
            <List.Item.Meta
              avatar={<FileTextOutlined style={{ fontSize: 22, color: 'var(--brand, #2563eb)' }} />}
              title={
                item.hasPublicPage ? (
                  <Link to={item.path}>{item.title}</Link>
                ) : (
                  <span>{item.title}</span>
                )
              }
              description={
                <span>
                  {item.version ? (
                    <Text type="secondary">Версия {item.version}</Text>
                  ) : (
                    <Text type="secondary">Версия уточняется при открытии</Text>
                  )}
                  {item.required ? (
                    <>
                      {' · '}
                      <Tag color="blue">Обязательный</Tag>
                    </>
                  ) : (
                    <>
                      {' · '}
                      <Tag>Opt-in</Tag>
                    </>
                  )}
                </span>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );
}
